/**
 * Shared pattern and helper functions for numeric expression validation.
 * Used by NumericExpressionDirective (CVA) and NumericPatternValidatorDirective.
 *
 * Accepted format: optional sign, integer or decimal part (comma or dot separator),
 * optional scientific exponent — leading and trailing whitespace is always ignored.
 *
 * Examples: 42  -3  +1.5  1,5  .5  1.  2.5E10  1e-3  "  42  "
 */
export const NUMERIC_EXPRESSION_PATTERN =
  /^[+-]?(?:[0-9]+(?:[\.,][0-9]*)?|[\.,][0-9]+)(?:[eE][+-]?[0-9]+)?$/;

/**
 * Returns true when the trimmed value matches the numeric expression pattern.
 * An empty or whitespace-only string returns false.
 */
export function isValidNumericExpression(value: string): boolean {
  return NUMERIC_EXPRESSION_PATTERN.test(value.trim());
}

/**
 * Parses a numeric expression string to a number.
 * Trims whitespace and normalises the decimal separator (comma → dot) before parsing.
 * Returns null for empty / whitespace-only input.
 * Assumes the caller has validated with isValidNumericExpression for non-null inputs.
 */
export function parseNumericExpression(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? parseFloat(trimmed.replace(',', '.')) : null;
}
